import { useCallback, useEffect, useState } from 'react';
import {
  fetchSponsoredAds,
  publishSponsoredAd,
  uploadAdImage,
  deleteSponsoredAd,
  AD_PAGE_TARGETS,
  ALL_PAGES_KEY,
  type SponsoredAd,
  type AdMediaType,
} from '@/lib/ads';

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-6">
      <h1 className="font-heading font-bold text-2xl text-foreground-50">{title}</h1>
      <p className="text-sm text-foreground-400 mt-1">{subtitle}</p>
    </div>
  );
}

const EMPTY_FORM = {
  sponsor: '',
  title: '',
  description: '',
  category: '',
  mediaType: 'image' as AdMediaType,
  mediaUrl: '',
  linkUrl: '',
  ctaLabel: '',
};

function formatTargetPages(pages: string[]): string {
  if (pages.length === 0) return 'Média (par défaut)';
  if (pages.includes(ALL_PAGES_KEY)) return 'Toutes les pages';
  return AD_PAGE_TARGETS.filter((p) => pages.includes(p.key))
    .map((p) => p.label)
    .join(', ');
}

function AdsSection() {
  const [ads, setAds] = useState<SponsoredAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [allPages, setAllPages] = useState(false);
  const [selectedPages, setSelectedPages] = useState<string[]>(['media']);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchSponsoredAds();
      setAds(data);
    } catch {
      setError('Impossible de charger les publicités. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (key: keyof typeof EMPTY_FORM, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      let mediaUrl = form.mediaUrl.trim();
      if (form.mediaType === 'image' && imageFile) {
        mediaUrl = await uploadAdImage(imageFile);
      }
      if (!mediaUrl) {
        throw new Error(
          form.mediaType === 'image'
            ? "Ajoutez une affiche (image) ou collez son URL."
            : 'Collez le lien de la vidéo.',
        );
      }

      await publishSponsoredAd({
        sponsor: form.sponsor.trim(),
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category.trim(),
        mediaType: form.mediaType,
        mediaUrl,
        linkUrl: form.linkUrl.trim(),
        ctaLabel: form.ctaLabel.trim(),
        targetPages: allPages ? [ALL_PAGES_KEY] : selectedPages,
      });

      setForm(EMPTY_FORM);
      setImageFile(null);
      setFormOpen(false);
      setAllPages(false);
      setSelectedPages(['media']);
      setSuccess('Publicité publiée avec succès.');
      await load();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Une erreur est survenue lors de la publication.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    setError('');
    try {
      await deleteSponsoredAd(id);
      setAds((prev) => prev.filter((a) => a.id !== id));
    } catch {
      setError('Impossible de supprimer cette publicité.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <SectionHeader
        title="Publicités sponsorisées"
        subtitle="Publier les bannières et vidéos sponsorisées affichées sur la page Média."
      />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20 mb-4">
          <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
          <p className="text-sm text-accent-400">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary-500/10 border border-secondary-300/20 mb-4">
          <i className="ri-checkbox-circle-line text-secondary-300 mt-0.5 shrink-0" />
          <p className="text-sm text-secondary-300">{success}</p>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-foreground-400">
          {ads.length} publicité{ads.length > 1 ? 's' : ''} active{ads.length > 1 ? 's' : ''}
        </span>
        <button
          onClick={() => {
            setFormOpen((v) => !v);
            setError('');
            setSuccess('');
          }}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          {formOpen ? 'Annuler' : 'Nouvelle publicité'}
        </button>
      </div>

      {formOpen && (
        <form
          onSubmit={handleSubmit}
          className="bg-background-100 border border-background-200/70 rounded-xl p-5 mb-6 space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Annonceur *
              </label>
              <input
                type="text"
                value={form.sponsor}
                onChange={(e) => update('sponsor', e.target.value)}
                placeholder="Ex : TogoPay"
                className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Catégorie
              </label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => update('category', e.target.value)}
                placeholder="Ex : Fintech, Mode, Finance"
                className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-300 mb-1.5">
              Titre / accroche *
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Ex : Envoyez de l'argent en un clic"
              className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-300 mb-1.5">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              maxLength={300}
              rows={2}
              placeholder="Décrivez brièvement l'offre…"
              className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-300 mb-1.5">
              Type de visuel *
            </label>
            <div className="flex gap-1 p-1 bg-background-50 rounded-lg w-fit">
              <button
                type="button"
                onClick={() => update('mediaType', 'image')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  form.mediaType === 'image'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-image-line mr-1" />
                Affiche (image)
              </button>
              <button
                type="button"
                onClick={() => update('mediaType', 'video')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  form.mediaType === 'video'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-video-line mr-1" />
                Vidéo
              </button>
            </div>
          </div>

          {form.mediaType === 'image' ? (
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Affiche (image)
              </label>
              {!imageFile ? (
                <div>
                  <label className="flex flex-col items-center justify-center gap-2 py-6 px-4 rounded-lg border-2 border-dashed border-background-300/60 hover:border-primary-400/50 cursor-pointer transition-colors">
                    <i className="ri-image-add-line text-2xl text-foreground-500" />
                    <span className="text-sm text-foreground-400 text-center">
                      Cliquez pour téléverser une affiche, ou collez son URL ci-dessous
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  <input
                    type="url"
                    value={form.mediaUrl}
                    onChange={(e) => update('mediaUrl', e.target.value)}
                    placeholder="ou URL de l'image (https://…)"
                    className="w-full mt-2 px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                    <i className="ri-image-line text-xl text-primary-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground-50 truncate">{imageFile.name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setImageFile(null)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-200/60 text-foreground-500 transition-colors shrink-0"
                    aria-label="Retirer l'image"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Lien de la vidéo *
              </label>
              <input
                type="url"
                value={form.mediaUrl}
                onChange={(e) => update('mediaUrl', e.target.value)}
                placeholder="https://… (fichier .mp4 ou lien de vidéo)"
                className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Lien de destination *
              </label>
              <input
                type="url"
                value={form.linkUrl}
                onChange={(e) => update('linkUrl', e.target.value)}
                placeholder="https://… (où redirige le clic)"
                className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Libellé du bouton
              </label>
              <input
                type="text"
                value={form.ctaLabel}
                onChange={(e) => update('ctaLabel', e.target.value)}
                placeholder="Ex : Découvrir"
                className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-300 mb-1.5">
              Pages d&apos;affichage *
            </label>
            <p className="text-xs text-foreground-400 mb-3">
              Choisissez où cette publicité doit apparaître. « Toutes les pages » l&apos;affiche
              partout ; sinon elle ne s&apos;affiche que sur les pages cochées ci-dessous.
            </p>

            <label className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-300/60 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={allPages}
                onChange={(e) => {
                  setAllPages(e.target.checked);
                  if (e.target.checked) setSelectedPages([]);
                }}
                className="accent-primary-500 w-4 h-4"
              />
              <span className="text-sm font-medium text-foreground-50">
                Toutes les pages de la plateforme
              </span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {AD_PAGE_TARGETS.map((page) => {
                const checked = selectedPages.includes(page.key);
                return (
                  <label
                    key={page.key}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      allPages
                        ? 'opacity-50 cursor-not-allowed bg-background-50 border-background-200/70'
                        : checked
                          ? 'bg-primary-500/10 border-primary-400/40'
                          : 'bg-background-50 border-background-300/60 hover:border-primary-400/30'
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={allPages}
                      checked={checked}
                      onChange={() => {
                        setSelectedPages((prev) =>
                          checked
                            ? prev.filter((p) => p !== page.key)
                            : [...prev, page.key],
                        );
                      }}
                      className="accent-primary-500 w-4 h-4"
                    />
                    <span className="text-sm text-foreground-50 whitespace-nowrap">
                      {page.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={
              saving ||
              !form.sponsor.trim() ||
              !form.title.trim() ||
              (!allPages && selectedPages.length === 0)
            }
            className="w-full px-4 py-3 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Publication en cours…
              </>
            ) : (
              <>
                <i className="ri-megaphone-line" />
                Publier la publicité
              </>
            )}
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
          Chargement des publicités…
        </div>
      ) : ads.length === 0 ? (
        <p className="text-foreground-400">Aucune publicité pour le moment.</p>
      ) : (
        <div className="space-y-3">
          {ads.map((ad) => (
            <div
              key={ad.id}
              className="bg-background-100 border border-background-200/70 rounded-xl p-4 flex items-center gap-4"
            >
              <div className="w-20 h-12 rounded-lg overflow-hidden bg-background-200/60 shrink-0">
                {ad.media_type === 'video' ? (
                  <video
                    src={ad.media_url}
                    muted
                    playsInline
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <img
                    src={ad.media_url}
                    alt={ad.title}
                    className="w-full h-full object-cover object-top"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {ad.media_type === 'video' ? 'Vidéo' : 'Affiche'}
                  </span>
                  {ad.category && (
                    <span className="text-xs text-foreground-400">{ad.category}</span>
                  )}
                </div>
                <p className="font-medium text-sm text-foreground-50 truncate">
                  {ad.sponsor} — {ad.title}
                </p>
                <p className="text-xs text-foreground-400 truncate flex items-center gap-1 mt-0.5">
                  <i className="ri-pages-line shrink-0" />
                  {formatTargetPages(ad.target_pages || [])}
                </p>
                {ad.link_url && (
                  <p className="text-xs text-foreground-400 truncate">{ad.link_url}</p>
                )}
              </div>
              <button
                onClick={() => handleDelete(ad.id)}
                disabled={deletingId === ad.id}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-foreground-800/80 text-foreground-100 hover:bg-foreground-700 transition-colors disabled:opacity-50 shrink-0"
                aria-label="Supprimer la publicité"
              >
                {deletingId === ad.id ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-delete-bin-6-line" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdsSection;